/**
 * DrawerLab™ 3D Item System
 * Supports GLB models (preferred) with procedural fallbacks
 *
 * GLB models expected in theme assets:
 *   item-socks.glb, item-boxers.glb, item-tshirts.glb,
 *   item-jeans.glb, item-sweaters.glb, item-bras.glb
 *
 * Each GLB should be a single low-poly mesh, ~200-500 verts,
 * centered at origin, standing upright, unit scale ~1.
 */

(function() {
  const THREE = window.THREE;
  if (!THREE) return;

  // ─── GLB LOADER SETUP ──────────────────────────────────────

  var gltfLoader = null;
  var modelCache = {};  // { 'socks': THREE.Group template }
  var loadQueue = [];   // callbacks waiting for models
  var loaderReady = false;

  function initLoader() {
    if (gltfLoader) return;

    // Load GLTFLoader from CDN (same Three.js version)
    var script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/loaders/GLTFLoader.js';
    script.onload = function() {
      gltfLoader = new THREE.GLTFLoader();
      loaderReady = true;
      // Process any queued loads
      loadQueue.forEach(function(fn) { fn(); });
      loadQueue = [];
    };
    document.head.appendChild(script);
  }

  // Start loading immediately
  initLoader();

  /**
   * Load a GLB model from theme assets
   * @param {string} itemType - e.g. 'socks'
   * @param {function} callback - called with (template) or null if not found
   */
  function loadModel(itemType, callback) {
    // Already cached?
    if (modelCache[itemType]) {
      callback(modelCache[itemType]);
      return;
    }

    // Check if GLB URL is registered
    var url = window.DRAWER_ITEM_MODELS && window.DRAWER_ITEM_MODELS[itemType];
    if (!url) {
      callback(null);
      return;
    }

    function doLoad() {
      gltfLoader.load(
        url,
        function(gltf) {
          var model = gltf.scene;
          // Log original size for debugging
          var box = new THREE.Box3().setFromObject(model);
          var size = new THREE.Vector3();
          box.getSize(size);
          console.log('DrawerItems: ' + itemType + ' original size:', size.x.toFixed(1), size.y.toFixed(1), size.z.toFixed(1));

          // Normalize to match procedural item sizes (~10-20 units)
          // Use item-specific glbScale if defined, default target height = 20 units
          var itemDef = window.DRAWER_ITEMS[itemType];
          var targetSize = (itemDef && itemDef.glbTargetHeight) || 20;
          var maxDim = Math.max(size.x, size.y, size.z);
          if (maxDim > 0) {
            var scale = targetSize / maxDim;
            model.scale.multiplyScalar(scale);
          }
          // Center horizontally, sit on ground
          box.setFromObject(model);
          var center = new THREE.Vector3();
          box.getCenter(center);
          model.position.x -= center.x;
          model.position.z -= center.z;
          model.position.y -= box.min.y;

          console.log('DrawerItems: ' + itemType + ' scaled to target ' + targetSize);
          modelCache[itemType] = model;
          callback(model);
        },
        undefined,
        function(err) {
          console.warn('DrawerItems: GLB not found for ' + itemType + ', using fallback');
          modelCache[itemType] = false; // mark as failed
          callback(null);
        }
      );
    }

    if (loaderReady) {
      doLoad();
    } else {
      loadQueue.push(doLoad);
    }
  }

  /**
   * Clone a cached GLB template and apply color
   */
  function cloneWithColor(template, color) {
    var clone = template.clone();
    clone.traverse(function(child) {
      if (child.isMesh) {
        child.material = child.material.clone();
        child.material.color.set(color);
      }
    });
    return clone;
  }

  // ─── ITEM DEFINITIONS ────────────────────────────────────────

  window.DRAWER_ITEMS = {

    // ─── SOKKEN ─────────────────────────────────────────────
    socks: {
      name: 'Sokken',
      foldedSize: { w: 45, d: 45, h: 55 },  // rolled pair: Ø45mm, 55mm tall
      mmScale: 3.75,  // procedural model ~12 wide → real 45mm
      perVak: 8,
      colors: [0x555566, 0x2a2a3a, 0xd4a898, 0x8899aa, 0xbbbbbb, 0x3d3d4d],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0x555566;

        // Try GLB cache first (synchronous if preloaded)
        if (modelCache['socks'] && modelCache['socks'] !== false) {
          return cloneWithColor(modelCache['socks'], c);
        }

        // Procedural fallback: rolled sock cylinder
        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.88, metalness: 0 });

        var bodyGeo = new THREE.CylinderGeometry(6, 6.3, 10, 12);
        var body = new THREE.Mesh(bodyGeo, mat);
        body.position.y = 5;
        group.add(body);

        // Spiral rings on top
        var spiralMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(1.3), roughness: 0.8
        });
        var ring1 = new THREE.Mesh(new THREE.TorusGeometry(2, 0.6, 6, 16), spiralMat);
        ring1.rotation.x = -Math.PI / 2;
        ring1.position.y = 10.2;
        group.add(ring1);

        var ring2 = new THREE.Mesh(new THREE.TorusGeometry(4.5, 0.5, 6, 16), spiralMat);
        ring2.rotation.x = -Math.PI / 2;
        ring2.position.y = 10.2;
        group.add(ring2);

        // Top cap
        var capMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.9), roughness: 0.85
        });
        var cap = new THREE.Mesh(new THREE.CylinderGeometry(6, 6, 0.5, 12), capMat);
        cap.position.y = 10;
        group.add(cap);

        return group;
      },
      stackPattern: 'grid'
    },

    // ─── BOXERSHORTS ────────────────────────────────────────
    boxers: {
      name: 'Boxershorts',
      foldedSize: { w: 90, d: 25, h: 110 },  // KonMari standing: 90w × 25d × 110h mm
      mmScale: 5.5,  // procedural ~14 wide → real 90mm
      perVak: 8,
      colors: [0x1a1a3a, 0x222222, 0x8B0000, 0x2d5a2d, 0x333355, 0x4a2a2a],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0x1a1a3a;

        if (modelCache['boxers'] && modelCache['boxers'] !== false) {
          return cloneWithColor(modelCache['boxers'], c);
        }

        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.8, metalness: 0 });
        var mesh = new THREE.Mesh(new THREE.BoxGeometry(14, 22, 4), mat);
        group.add(mesh);

        var bandMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(1.3), roughness: 0.7
        });
        var band = new THREE.Mesh(new THREE.BoxGeometry(14.2, 2, 4.2), bandMat);
        band.position.y = 9;
        group.add(band);

        return group;
      },
      stackPattern: 'vertical'
    },

    // ─── T-SHIRTS ───────────────────────────────────────────
    tshirts: {
      name: 'T-shirts',
      foldedSize: { w: 250, d: 200, h: 25 },  // flat folded: 250w × 200d × 25h mm
      mmScale: 14,  // procedural ~18 wide → real 250mm
      perVak: 6,
      colors: [0xffffff, 0x222222, 0x2c3e50, 0x7f8c8d, 0x1a5276, 0x8B0000, 0xf0e68c],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0xffffff;

        if (modelCache['tshirts'] && modelCache['tshirts'] !== false) {
          return cloneWithColor(modelCache['tshirts'], c);
        }

        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.82, metalness: 0 });

        // Flat folded body
        var body = new THREE.Mesh(new THREE.BoxGeometry(18, 2.5, 14), mat);
        body.position.y = 1.25;
        group.add(body);

        // Fold line
        var foldMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.92), roughness: 0.85
        });
        var fold = new THREE.Mesh(new THREE.BoxGeometry(18.1, 0.6, 14.1), foldMat);
        fold.position.y = 2.6;
        group.add(fold);

        // Collar (half torus)
        var collarMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.85), roughness: 0.8
        });
        var collar = new THREE.Mesh(new THREE.TorusGeometry(3, 0.7, 6, 10, Math.PI), collarMat);
        collar.rotation.x = -Math.PI / 2;
        collar.position.set(0, 2.8, -5);
        group.add(collar);

        // Collar fill
        var neck = new THREE.Mesh(
          new THREE.SphereGeometry(2.5, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), collarMat
        );
        neck.rotation.x = Math.PI;
        neck.position.set(0, 3.2, -5);
        neck.scale.set(1.2, 0.3, 0.6);
        group.add(neck);

        return group;
      },
      stackPattern: 'stacked'
    },

    // ─── JEANS ──────────────────────────────────────────────
    jeans: {
      name: 'Jeans',
      foldedSize: { w: 280, d: 220, h: 35 },  // flat folded: 280w × 220d × 35h mm
      mmScale: 14,  // procedural ~20 wide → real 280mm
      perVak: 4,
      colors: [0x1a3a6a, 0x0d2137, 0x6a8a7a, 0x1a1a2a],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0x1a3a6a;

        if (modelCache['jeans'] && modelCache['jeans'] !== false) {
          return cloneWithColor(modelCache['jeans'], c);
        }

        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.92, metalness: 0 });

        // Flat folded body
        var body = new THREE.Mesh(new THREE.BoxGeometry(20, 3, 16), mat);
        body.position.y = 1.5;
        group.add(body);

        // Fold lines
        var foldMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.85), roughness: 0.9
        });
        for (var i = 0; i < 3; i++) {
          var line = new THREE.Mesh(new THREE.BoxGeometry(20.1, 0.15, 16.1), foldMat);
          line.position.y = 0.8 + i * 0.9;
          group.add(line);
        }

        // Back pocket
        var pocketMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.9), roughness: 0.88
        });
        var pocket = new THREE.Mesh(new THREE.BoxGeometry(7, 0.3, 6), pocketMat);
        pocket.position.set(3, 3.15, -1);
        group.add(pocket);

        // Pocket stitch
        var stitchMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(1.2), roughness: 0.8
        });
        var sx = 7.2, sz = 6.2, st = 0.25;
        [
          { w: sx, d: st, x: 3, z: -1 - sz/2 + st/2 },
          { w: sx, d: st, x: 3, z: -1 + sz/2 - st/2 },
          { w: st, d: sz, x: 3 - sx/2 + st/2, z: -1 },
          { w: st, d: sz, x: 3 + sx/2 - st/2, z: -1 }
        ].forEach(function(e) {
          var em = new THREE.Mesh(new THREE.BoxGeometry(e.w, 0.15, e.d), stitchMat);
          em.position.set(e.x, 3.32, e.z);
          group.add(em);
        });

        // Leather label
        var label = new THREE.Mesh(
          new THREE.BoxGeometry(4, 0.2, 2),
          new THREE.MeshStandardMaterial({ color: 0xB87333, roughness: 0.7 })
        );
        label.position.set(-5, 3.2, -5);
        group.add(label);

        group.rotation.y = 0.05;
        return group;
      },
      stackPattern: 'stacked'
    },

    // ─── TRUIEN ─────────────────────────────────────────────
    sweaters: {
      name: 'Truien',
      foldedSize: { w: 300, d: 280, h: 50 },  // flat folded: 300w × 280d × 50h mm
      mmScale: 16,  // procedural ~18 wide → real 300mm
      perVak: 3,
      colors: [0x333333, 0x8B4513, 0x2d5a2d, 0xd4c5a9, 0x800020],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0x333333;

        if (modelCache['sweaters'] && modelCache['sweaters'] !== false) {
          return cloneWithColor(modelCache['sweaters'], c);
        }

        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.9, metalness: 0 });
        var mesh = new THREE.Mesh(new THREE.BoxGeometry(18, 8, 18), mat);
        group.add(mesh);

        return group;
      },
      stackPattern: 'stacked'
    },

    // ─── BH's ───────────────────────────────────────────────
    bras: {
      name: "BH's",
      foldedSize: { w: 160, d: 140, h: 35 },  // stacked bra: 160w × 140d × 35h mm
      mmScale: 10,  // procedural ~16 wide → real 160mm
      perVak: 4,
      colors: [0x222222, 0xf5e6d3, 0xd4a5a5, 0x8B0000, 0xffffff],
      glbTargetHeight: 20,
      generate: function(color) {
        var c = color || 0x222222;

        if (modelCache['bras'] && modelCache['bras'] !== false) {
          return cloneWithColor(modelCache['bras'], c);
        }

        var group = new THREE.Group();
        var mat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.7, metalness: 0 });
        var cupGeo = new THREE.SphereGeometry(6, 8, 6, 0, Math.PI);

        var cup1 = new THREE.Mesh(cupGeo, mat);
        cup1.position.x = -5;
        cup1.rotation.x = -Math.PI / 2;
        group.add(cup1);

        var cup2 = new THREE.Mesh(cupGeo, mat.clone());
        cup2.position.x = 5;
        cup2.rotation.x = -Math.PI / 2;
        group.add(cup2);

        var bandMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(c).multiplyScalar(0.9), roughness: 0.8
        });
        var band = new THREE.Mesh(new THREE.BoxGeometry(16, 1, 3), bandMat);
        band.position.y = -3;
        group.add(band);

        return group;
      },
      stackPattern: 'stacked'
    }
  };

  // ─── PRELOAD GLB MODELS ─────────────────────────────────────
  // Models are registered via window.DRAWER_ITEM_MODELS = { socks: '...url...' }
  // Set this before drawer-items.js loads, or call DRAWER_ITEMS.preloadModels() after

  window.DRAWER_ITEMS.preloadModels = function(callback) {
    var types = Object.keys(window.DRAWER_ITEMS).filter(function(k) {
      return k !== 'placeInCompartment' && k !== 'preloadModels';
    });
    var remaining = types.length;
    var loaded = 0;

    types.forEach(function(type) {
      loadModel(type, function(template) {
        if (template) loaded++;
        remaining--;
        if (remaining <= 0) {
          console.log('DrawerItems: ' + loaded + '/' + types.length + ' GLB models loaded');
          if (callback) callback(loaded);
        }
      });
    });
  };

  // Auto-preload if model URLs are already registered
  if (window.DRAWER_ITEM_MODELS) {
    // Wait for loader to be ready
    var checkLoader = setInterval(function() {
      if (loaderReady) {
        clearInterval(checkLoader);
        window.DRAWER_ITEMS.preloadModels();
      }
    }, 50);
  }

  // ─── ITEM PLACER: fills a compartment with items ──────────

  window.DRAWER_ITEMS.placeInCompartment = function(scene, itemType, bounds, profileHeight) {
    const item = window.DRAWER_ITEMS[itemType];
    if (!item) return [];

    const meshes = [];
    const { minX, maxX, minZ, maxZ, y } = bounds;
    const compW = maxX - minX;   // compartment width in mm (= real mm, 1:1 with viewport)
    const compD = maxZ - minZ;   // compartment depth in mm
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const ph = Math.min(profileHeight || 90, 90);

    // mmScale converts procedural model units to real mm
    // After scaling, 1 unit in the model = 1mm in the viewport
    const S = item.mmScale || 1;
    const fs = item.foldedSize; // real-world size in mm { w, d, h }

    const colors = item.colors;
    let colorIdx = 0;
    const pad = 10; // mm padding from compartment walls

    if (item.stackPattern === 'grid') {
      // Grid: items in rows and columns (rolled socks)
      // Each item occupies foldedSize.w × foldedSize.d mm of floor space
      const cols = Math.max(1, Math.floor((compW - pad) / fs.w));
      const rows = Math.max(1, Math.floor((compD - pad) / fs.d));
      const maxItems = Math.min(cols * rows, item.perVak);

      let count = 0;
      for (let r = 0; r < rows && count < maxItems; r++) {
        for (let c = 0; c < cols && count < maxItems; c++) {
          const mesh = item.generate(colors[colorIdx % colors.length]);
          mesh.position.set(
            minX + pad/2 + (c + 0.5) * ((compW - pad) / cols),
            y,
            minZ + pad/2 + (r + 0.5) * ((compD - pad) / rows)
          );
          mesh.scale.setScalar(S);
          scene.add(mesh);
          meshes.push(mesh);
          colorIdx++;
          count++;
        }
      }

    } else if (item.stackPattern === 'vertical') {
      // Vertical KonMari: items standing side by side (boxers)
      // Each item is foldedSize.d mm thick when standing
      const slotW = fs.d + 3; // spacing = item depth + small gap
      const maxItems = Math.min(Math.max(1, Math.floor((compW - pad) / slotW)), item.perVak);
      const totalW = maxItems * slotW;
      const startX = centerX - totalW / 2 + slotW / 2;

      for (let i = 0; i < maxItems; i++) {
        const mesh = item.generate(colors[colorIdx % colors.length]);
        mesh.position.set(
          startX + i * slotW,
          y,
          centerZ
        );
        mesh.scale.setScalar(S);
        scene.add(mesh);
        meshes.push(mesh);
        colorIdx++;
      }

    } else if (item.stackPattern === 'stacked') {
      // Stacked: items on top of each other (t-shirts, jeans, sweaters, bras)
      // Each item is foldedSize.h mm tall
      const maxByHeight = Math.max(1, Math.floor((ph - 10) / fs.h));
      const maxItems = Math.min(maxByHeight, item.perVak);

      for (let i = 0; i < maxItems; i++) {
        const mesh = item.generate(colors[colorIdx % colors.length]);
        mesh.position.set(
          centerX + (Math.random() - 0.5) * 4,
          y + i * fs.h,
          centerZ + (Math.random() - 0.5) * 4
        );
        mesh.scale.setScalar(S);
        mesh.rotation.y = (Math.random() - 0.5) * 0.06;
        scene.add(mesh);
        meshes.push(mesh);
        colorIdx++;
      }
    }

    return meshes;
  };

})();
